import React, { useState, useEffect, useMemo } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ITEMS_PER_PAGE = 20;

const ClientSharesHistory = ({ isOpen, onClose }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Filters
  const [searchName, setSearchName] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showRevoked, setShowRevoked] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchShares();
      setCurrentPage(1); // Reset page when opening
    }
  }, [isOpen]);

  const fetchShares = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${API}/my-client-shares`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des partages');
      }
      
      const data = await response.json();
      // Sort by date descending (most recent first)
      const sortedData = data.sort((a, b) => new Date(b.shared_at) - new Date(a.shared_at));
      setShares(sortedData);
    } catch (err) {
      console.error('Error fetching shares:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filtered shares
  const filteredShares = useMemo(() => {
    return shares.filter(share => {
      // Filter by revoked status
      if (!showRevoked && share.is_revoked) return false;
      
      // Filter by name/email
      if (searchName) {
        const search = searchName.toLowerCase();
        const nameMatch = share.client_name?.toLowerCase().includes(search);
        const emailMatch = share.client_email?.toLowerCase().includes(search);
        const studyMatch = share.study_name?.toLowerCase().includes(search);
        if (!nameMatch && !emailMatch && !studyMatch) return false;
      }
      
      // Filter by date
      if (dateFrom) {
        const shareDate = new Date(share.shared_at).setHours(0,0,0,0);
        const fromDate = new Date(dateFrom).setHours(0,0,0,0);
        if (shareDate < fromDate) return false;
      }
      
      if (dateTo) {
        const shareDate = new Date(share.shared_at).setHours(23,59,59,999);
        const toDate = new Date(dateTo).setHours(23,59,59,999);
        if (shareDate > toDate) return false;
      }
      
      return true;
    });
  }, [shares, searchName, dateFrom, dateTo, showRevoked]);

  // Pagination
  const totalPages = Math.ceil(filteredShares.length / ITEMS_PER_PAGE);
  const paginatedShares = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredShares.slice(start, end);
  }, [filteredShares, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchName, dateFrom, dateTo, showRevoked]);

  const handleRevoke = async (shareToken) => {
    if (!window.confirm('Êtes-vous sûr de vouloir révoquer cet accès ? Le client ne pourra plus consulter l\'étude.')) {
      return;
    }
    
    try {
      setRevoking(shareToken);
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${API}/client-share/${shareToken}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la révocation');
      }
      
      await fetchShares();
    } catch (err) {
      console.error('Error revoking share:', err);
      alert('Erreur lors de la révocation de l\'accès');
    } finally {
      setRevoking(null);
    }
  };

  const handleDeletePermanent = async (shareToken) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement ce partage ? Cette action est irréversible.')) {
      return;
    }
    
    try {
      setDeleting(shareToken);
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${API}/client-share/${shareToken}/permanent`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }
      
      await fetchShares();
    } catch (err) {
      console.error('Error deleting share:', err);
      alert('Erreur lors de la suppression du partage');
    } finally {
      setDeleting(null);
    }
  };

  const copyLink = (shareToken) => {
    const link = `${window.location.origin}/study/public/${shareToken}`;
    navigator.clipboard.writeText(link);
    alert('Lien copié dans le presse-papier !');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const clearFilters = () => {
    setSearchName('');
    setDateFrom('');
    setDateTo('');
    setShowRevoked(true);
  };

  if (!isOpen) return null;

  const activeCount = shares.filter(s => !s.is_revoked).length;
  const revokedCount = shares.filter(s => s.is_revoked).length;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '95%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '1.5rem',
              color: '#191d1e',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              Historique des partages clients
            </h2>
            <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.875rem' }}>
              {activeCount} actif(s) • {revokedCount} révoqué(s)
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#9ca3af',
              padding: '0.5rem'
            }}
            data-testid="close-history-modal"
          >
            ×
          </button>
        </div>

        {/* Filters */}
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #e5e7eb',
          background: '#f9fafb'
        }}>
          <div style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'flex-end',
            flexWrap: 'wrap'
          }}>
            {/* Search by name/email */}
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                Rechercher par nom / email
              </label>
              <input
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="Nom du client, email..."
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Date from */}
            <div style={{ minWidth: '150px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                Du
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Date to */}
            <div style={{ minWidth: '150px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                Au
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Show revoked toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                id="showRevoked"
                checked={showRevoked}
                onChange={(e) => setShowRevoked(e.target.checked)}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <label htmlFor="showRevoked" style={{ fontSize: '0.875rem', color: '#374151', cursor: 'pointer' }}>
                Afficher révoqués
              </label>
            </div>
            
            {/* Clear filters */}
            <button
              onClick={clearFilters}
              style={{
                padding: '0.5rem 1rem',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: '#6b7280',
                cursor: 'pointer'
              }}
            >
              Effacer filtres
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1.5rem'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #e5e7eb',
                borderTopColor: '#397DFF',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 1rem'
              }} />
              <p style={{ color: '#666' }}>Chargement...</p>
              <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
              `}</style>
            </div>
          ) : error ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#dc2626'
            }}>
              <p>{error}</p>
              <button
                onClick={fetchShares}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  background: '#397DFF',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Réessayer
              </button>
            </div>
          ) : filteredShares.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#666'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
              <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
                {shares.length === 0 ? 'Aucun partage client' : 'Aucun résultat pour ces filtres'}
              </p>
              <p style={{ fontSize: '0.875rem' }}>
                {shares.length === 0 
                  ? 'Utilisez le bouton "Partager par email" sur une étude pour envoyer un lien à un client.'
                  : 'Modifiez vos critères de recherche ou effacez les filtres.'}
              </p>
            </div>
          ) : (
            <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {paginatedShares.map((share) => (
                <div
                  key={share.token}
                  style={{
                    background: share.is_revoked ? '#f9fafb' : 'white',
                    border: `1px solid ${share.is_revoked ? '#e5e7eb' : '#d1d5db'}`,
                    borderRadius: '10px',
                    padding: '1.25rem',
                    opacity: share.is_revoked ? 0.7 : 1
                  }}
                  data-testid={`share-item-${share.token}`}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '1rem'
                  }}>
                    <div>
                      <h3 style={{
                        margin: '0 0 0.5rem 0',
                        fontSize: '1.125rem',
                        color: '#191d1e'
                      }}>
                        {share.client_name || share.client_email}
                      </h3>
                      <p style={{
                        margin: 0,
                        fontSize: '0.875rem',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span>📧</span>
                        {share.client_email}
                        {share.client_name && (
                          <span style={{ color: '#9ca3af' }}>• {share.study_name}</span>
                        )}
                      </p>
                    </div>
                    
                    {/* Status badge */}
                    <div style={{
                      padding: '0.375rem 0.75rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      background: share.is_revoked 
                        ? '#fee2e2' 
                        : share.last_viewed_at 
                          ? '#dcfce7' 
                          : '#fef3c7',
                      color: share.is_revoked 
                        ? '#991b1b' 
                        : share.last_viewed_at 
                          ? '#166534' 
                          : '#92400e'
                    }}>
                      {share.is_revoked 
                        ? 'Révoqué' 
                        : share.last_viewed_at 
                          ? 'Consulté' 
                          : 'Non consulté'}
                    </div>
                  </div>

                  {/* Details */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '0.75rem',
                    marginBottom: '1rem',
                    fontSize: '0.8125rem',
                    color: '#6b7280'
                  }}>
                    <div>
                      <span>Partagé le:</span>
                      <span style={{ marginLeft: '0.5rem', color: '#374151' }}>
                        {formatDate(share.shared_at)}
                      </span>
                    </div>
                    {share.last_viewed_at && (
                      <div>
                        <span>👁️ Consulté le:</span>
                        <span style={{ marginLeft: '0.5rem', color: '#374151' }}>
                          {formatDate(share.last_viewed_at)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{
                    display: 'flex',
                    gap: '0.75rem',
                    borderTop: '1px solid #e5e7eb',
                    paddingTop: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    {!share.is_revoked ? (
                      <>
                        <button
                          onClick={() => copyLink(share.token)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#eff6ff',
                            color: '#1d4ed8',
                            border: '1px solid #bfdbfe',
                            borderRadius: '6px',
                            fontSize: '0.8125rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.375rem'
                          }}
                          data-testid={`copy-link-${share.token}`}
                        >
                          Copier le lien
                        </button>
                        <button
                          onClick={() => window.open(`/study/public/${share.token}`, '_blank')}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#f3f4f6',
                            color: '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.8125rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.375rem'
                          }}
                          data-testid={`preview-link-${share.token}`}
                        >
                          👁️ Prévisualiser
                        </button>
                        <button
                          onClick={() => handleRevoke(share.token)}
                          disabled={revoking === share.token}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#fee2e2',
                            color: '#991b1b',
                            border: '1px solid #fecaca',
                            borderRadius: '6px',
                            fontSize: '0.8125rem',
                            fontWeight: '500',
                            cursor: revoking === share.token ? 'not-allowed' : 'pointer',
                            opacity: revoking === share.token ? 0.6 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.375rem',
                            marginLeft: 'auto'
                          }}
                          data-testid={`revoke-link-${share.token}`}
                        >
                          {revoking === share.token ? 'Révocation...' : 'Révoquer l\'accès'}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleDeletePermanent(share.token)}
                        disabled={deleting === share.token}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#fef2f2',
                          color: '#991b1b',
                          border: '1px solid #fecaca',
                          borderRadius: '6px',
                          fontSize: '0.8125rem',
                          fontWeight: '500',
                          cursor: deleting === share.token ? 'not-allowed' : 'pointer',
                          opacity: deleting === share.token ? 0.6 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.375rem'
                        }}
                        data-testid={`delete-link-${share.token}`}
                      >
                        {deleting === share.token ? 'Suppression...' : 'Supprimer définitivement'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.5rem',
                marginTop: '1.5rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e5e7eb'
              }}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem 1rem',
                    background: currentPage === 1 ? '#f3f4f6' : 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.5 : 1
                  }}
                >
                  ← Précédent
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      background: currentPage === page ? '#397DFF' : 'white',
                      color: currentPage === page ? 'white' : '#374151',
                      border: '1px solid',
                      borderColor: currentPage === page ? '#397DFF' : '#d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: currentPage === page ? '600' : '400',
                      minWidth: '40px'
                    }}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '0.5rem 1rem',
                    background: currentPage === totalPages ? '#f3f4f6' : 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    opacity: currentPage === totalPages ? 0.5 : 1
                  }}
                >
                  Suivant →
                </button>
              </div>
            )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
            Page {currentPage}/{totalPages || 1} • {filteredShares.length} partage(s) sur {shares.length}
          </p>
          <button
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#397DFF',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
            data-testid="close-history-btn"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientSharesHistory;
